import { Icon } from '@sr/ui-library';
import { useEffect, useState } from 'react';
import { Button, Input } from 'react-daisyui';
import { useSearchParams } from 'react-router-dom';
import { PAYOUT_SEARCH } from '../constant/Common';
import { useDebounce } from '../hooks/useDebounce';
import { Spinner } from './Loader';

interface IAppSearchField {
  className?: string;
  /**
   * When set, this writes the search value (and any paired page reset) instead
   * of AppSearchField mutating the URL itself.
   */
  commitSearch?: (searchValue: string) => void;
  loading?: boolean;
  onSearchChange?: (searchValue: string) => void;
  placeHolder?: string;
  searchKey?: string;
  width?: 'w-36' | 'w-44' | 'w-52' | 'w-60' | 'w-72' | 'w-96' | 'w-full';
}

/**
 *
 * @param onSearchChange Optional, we can get the value via URL params or onSearchChange
 * @param placeHolder Optional, set input placeholder
 * @param searchKey Optional, searchKey let you update the URL search params
 * @deprecated Use AppSearchInput from './AppSearchInput' for new search inputs — this MSR2-era component uses a different design system.
 */
const AppSearchField = ({
  className,
  commitSearch,
  loading = false,
  onSearchChange,
  placeHolder = 'Search',
  searchKey = PAYOUT_SEARCH,
  width = 'w-full',
}: IAppSearchField) => {
  // Used useState to handle search value to improve typing performance
  // Avoid using params value to apply directly to input value, It makes typing feels very slow.
  // TODO: add to coding standard
  const [searchValue, setSearchValue] = useState<string>('');
  const [searchParams, setSearchParams] = useSearchParams();

  const handleSetParams = (searchValue: string) => {
    if (commitSearch) {
      commitSearch(searchValue);
    } else {
      setSearchParams(
        (current) => {
          current.set(searchKey, searchValue);

          return current;
        },
        { replace: true },
      );
    }

    if (onSearchChange) {
      onSearchChange(searchValue);
    }
  };

  const handleDebounced = useDebounce(handleSetParams, 500);

  const handleChange = (searchValue: string) => {
    setSearchValue(searchValue);

    handleDebounced(searchValue);
  };

  const handleClear = () => {
    setSearchValue('');

    if (commitSearch) {
      commitSearch('');

      return;
    }

    // Create new URLSearchParams object to preserve other params
    const newParams = new URLSearchParams(searchParams);

    // Remove search parameters
    newParams.delete(searchKey);

    setSearchParams(newParams, { replace: true });
  };

  useEffect(() => {
    const searchValue = searchParams.get(searchKey) || '';

    setSearchValue(searchValue);

    if (onSearchChange) onSearchChange(searchValue);
  }, [searchParams, searchKey, onSearchChange]);

  return (
    <div className="relative">
      <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
        {loading ? (
          <Spinner className="size-6" />
        ) : (
          <Icon icon="search" size="xl" />
        )}
      </div>
      <label className="sr-only" htmlFor="search-field">
        Search Field
      </label>
      <Input
        id="search-field"
        className={`block border-solid bg-white pl-11 pr-9 text-base placeholder-transparent lg:${width} border-1 focus:border-1 active:border-1 search focus:border-solid focus:border-sr-dark-outline focus:bg-white focus:outline-0 active:border-solid active:border-sr-dark-outline xl:w-[350px] xl:placeholder-gray-400 ${className}`}
        placeholder={placeHolder}
        value={searchValue}
        onChange={(event) => handleChange(event.target.value)}
      />
      {searchValue && (
        <div className="absolute inset-y-0 right-0 flex items-center pr-3">
          <Button
            className="w-6"
            color="ghost"
            shape="circle"
            size="xs"
            startIcon={<Icon icon="x" size="xl" />}
            onClick={handleClear}
          >
            <span className="sr-only">Clear</span>
          </Button>
        </div>
      )}
    </div>
  );
};

export default AppSearchField;
